import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { writeAuditLog } from '@/lib/audit';
import { pauseReasonLabel, pauseScopeLabel, type PauseReason, type PauseRow } from '@/lib/pauses';

// Reads and writes for assembly_order_pauses. The database stamps who and
// when (see 20261105120000_assembly_order_pauses.sql), so nothing here sends
// timestamps or author ids.

const SELECT = `*,
  paused_by_tech:technicians!paused_by(name, alias),
  reviewed_by_tech:technicians!reviewed_by(name, alias)`;

/** Every query here lives under this key, so one invalidation refreshes all. */
export const PAUSES_KEY = ['assembly-order-pauses'] as const;

export async function fetchOrderPauses(orderId: string): Promise<PauseRow[]> {
  const { data, error } = await supabase
    .from('assembly_order_pauses')
    .select(SELECT)
    .eq('assembly_order_id', orderId)
    .order('paused_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as PauseRow[];
}

/** Pauses for many orders, keyed by order id. Chunked to keep URLs short. */
export async function fetchPausesForOrders(orderIds: string[]): Promise<Map<string, PauseRow[]>> {
  const byOrder = new Map<string, PauseRow[]>();
  for (let i = 0; i < orderIds.length; i += 100) {
    const { data, error } = await supabase
      .from('assembly_order_pauses')
      .select('id, assembly_order_id, unit_number, reason, paused_at, resumed_at, review_status')
      .in('assembly_order_id', orderIds.slice(i, i + 100));
    if (error) throw error;
    for (const p of (data ?? []) as PauseRow[]) {
      const list = byOrder.get(p.assembly_order_id);
      if (list) list.push(p);
      else byOrder.set(p.assembly_order_id, [p]);
    }
  }
  return byOrder;
}

/** Every pause still running, across all orders - for the pipeline boards. */
export async function fetchOpenPauses(): Promise<PauseRow[]> {
  const { data, error } = await supabase
    .from('assembly_order_pauses')
    .select(SELECT)
    .is('resumed_at', null);
  if (error) throw error;
  return (data ?? []) as PauseRow[];
}

/** Pauses on the order that still need a decision. */
export async function countUnreviewedPauses(orderId: string): Promise<number> {
  const { count, error } = await supabase
    .from('assembly_order_pauses')
    .select('id', { count: 'exact', head: true })
    .eq('assembly_order_id', orderId)
    .or('review_status.eq.pending,resumed_at.is.null');
  if (error) throw error;
  return count ?? 0;
}

/**
 * Throws when the order has a pause awaiting review. Called before posting
 * starts: the database refuses the status change too, but by then the
 * Assembly Orders page has already deducted stock.
 */
export async function assertPausesReviewed(orderId: string): Promise<void> {
  const n = await countUnreviewedPauses(orderId);
  if (n > 0) {
    throw new Error(`${n} pause${n > 1 ? 's' : ''} on this order still need${n > 1 ? '' : 's'} approving or rejecting before it can be posted.`);
  }
}

// ── Hooks ───────────────────────────────────────────────────────────────────

export function useOrderPauses(orderId: string | null | undefined) {
  return useQuery({
    queryKey: [...PAUSES_KEY, 'order', orderId],
    queryFn: () => fetchOrderPauses(orderId!),
    enabled: !!orderId,
  });
}

export function useOpenPauses() {
  return useQuery({
    queryKey: [...PAUSES_KEY, 'open'],
    queryFn: fetchOpenPauses,
    refetchInterval: 60_000,
  });
}

interface OrderRef { id: string; order_number?: string | null }

export function usePauseMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: PAUSES_KEY });

  const pause = useMutation({
    mutationFn: async ({ order, unit, reason, note }: { order: OrderRef; unit: number | null; reason: PauseReason; note: string }) => {
      const { error } = await supabase.from('assembly_order_pauses').insert({
        assembly_order_id: order.id,
        unit_number: unit,
        reason,
        note: note.trim() || null,
      });
      if (error) {
        if (error.code === '23505') throw new Error(`${unit == null ? 'The order' : `Unit ${unit}`} is already paused.`);
        throw error;
      }
      writeAuditLog({
        action: 'assembly_order_paused',
        entity_type: 'assembly_order',
        entity_id: order.id,
        entity_label: order.order_number ?? undefined,
        new_value: { unit, reason, note: note.trim() || null },
        notes: `${pauseScopeLabel({ unit_number: unit })} paused: ${pauseReasonLabel(reason)}`,
      });
    },
    onSuccess: (_, { unit }) => {
      refresh();
      toast.success(`${unit == null ? 'Order' : `Unit ${unit}`} paused. The clock has stopped until you resume.`);
    },
    onError: (e: any) => toast.error(e.message || 'Could not pause'),
  });

  const resume = useMutation({
    mutationFn: async ({ order, pause: p }: { order: OrderRef; pause: PauseRow }) => {
      // resumed_at is stamped by the database; any value here is replaced.
      const { data, error } = await supabase
        .from('assembly_order_pauses')
        .update({ resumed_at: new Date().toISOString() })
        .eq('id', p.id)
        .is('resumed_at', null)
        .select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('This pause had already ended.');
      writeAuditLog({
        action: 'assembly_order_resumed',
        entity_type: 'assembly_order',
        entity_id: order.id,
        entity_label: order.order_number ?? undefined,
        new_value: { pause_id: p.id, unit: p.unit_number },
        notes: `${pauseScopeLabel(p)} resumed (paused for ${pauseReasonLabel(p.reason)})`,
      });
    },
    onSuccess: (_, { pause: p }) => {
      refresh();
      toast.success(`${p.unit_number == null ? 'Order' : `Unit ${p.unit_number}`} resumed.`);
    },
    onError: (e: any) => toast.error(e.message || 'Could not resume'),
  });

  const review = useMutation({
    mutationFn: async ({ order, pause: p, status, note }: { order: OrderRef; pause: PauseRow; status: 'approved' | 'rejected' | 'pending'; note?: string | null }) => {
      const { error } = await supabase
        .from('assembly_order_pauses')
        .update({ review_status: status, review_note: note?.trim() || null })
        .eq('id', p.id);
      if (error) throw error;
      writeAuditLog({
        action: 'assembly_order_pause_reviewed',
        entity_type: 'assembly_order',
        entity_id: order.id,
        entity_label: order.order_number ?? undefined,
        old_value: { pause_id: p.id, review_status: p.review_status },
        new_value: { pause_id: p.id, review_status: status, review_note: note?.trim() || null },
        notes: `${pauseScopeLabel(p)} pause (${pauseReasonLabel(p.reason)}) ${status === 'pending' ? 'review undone' : status}`,
      });
    },
    onSuccess: refresh,
    onError: (e: any) => toast.error(e.message || 'Could not save the review'),
  });

  return { pause, resume, review };
}
